import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CAMINHOS,
  type EspectadorDaSessao,
  type SentidoDoAcesso,
  type VisibilidadeDaSessao,
} from '@remoto/shared';
import { chamar } from '../../api';
import { BotaoLeve } from '../layout';
import { CampoDeEscolha } from '../campos';
import { mensagemDoErro } from '../../erros';

/**
 * "Quem enxerga ESTA conversa" — o controle de exceção por sessão, só ADMIN
 * (ADR-0020).
 *
 * ## Por que ele mora aqui e não na tela de contas
 *
 * Até o ADR-0019 a marcação por sessão era editada na tela de contas: uma
 * caixa por sessão, por conta. Com as 192 sessões medidas em 2026-09-21 isso
 * virou uma lista inutilizável — e, pior, a pergunta que ela respondia não é
 * a que alguém faz. Ninguém abre a tela de contas para decidir uma conversa;
 * abre a conversa e pergunta quem a vê. O dono pediu a troca com essas
 * palavras, e a base da visibilidade virou o PROJETO.
 *
 * ## O modelo em uma frase
 *
 * **Projeto é a base, sessão é a exceção — nos dois sentidos.** Cada conta
 * aparece com o que o PROJETO já decide (`motivoBase`) e com a exceção que
 * alguém pôs por cima. As três escolhas por linha são:
 *
 * - **segue o projeto** (sem exceção): apagar a exceção devolve a conversa ao
 *   destino do projeto. É o desfazer, e é por isso que ele não é o mesmo que
 *   "ver" — devolver ao projeto pode significar ver OU não ver.
 * - **sempre vê**: abre uma conversa de projeto não liberado.
 * - **nunca vê**: esconde uma conversa que o projeto abriria.
 *
 * A precedência de verdade mora no servidor (`veASessao`), e esta tela só a
 * desenha: o campo `ve` de cada linha é o veredito do MESMO resolver que
 * decide o push — nunca uma conta feita aqui.
 */

const ESCOLHAS = [
  { id: 'HERDA', rotulo: 'segue o projeto', descricao: 'sem exceção: vale o que o projeto decide' },
  { id: 'PERMITIR', rotulo: 'sempre vê', descricao: 'abre esta conversa mesmo sem o projeto' },
  { id: 'NEGAR', rotulo: 'nunca vê', descricao: 'esconde esta conversa mesmo com o projeto' },
] as const;

type Escolha = (typeof ESCOLHAS)[number]['id'];

const MOTIVOS: Record<EspectadorDaSessao['motivoBase'], string> = {
  ADMIN: 'administrador — vê toda conversa',
  VER_TUDO: 'pela chave “pode ver tudo”',
  DONO: 'criou esta conversa',
  PROJETO: 'pelo projeto liberado',
  MESTRE_POR_FLAG: 'pela chave de leitura da mestre',
  NENHUM: 'o projeto desta conversa não está liberado para ela',
};

const ERROS = {
  usuario_desconhecido: 'Uma das contas não existe mais. Recarregue a página.',
  excecao_nao_vale_para_admin:
    'Administrador vê toda conversa: a exceção não valeria, então ela não é gravada.',
};

export function QuemVeEstaConversa({ sessaoId }: { sessaoId: string }): React.JSX.Element {
  const cliente = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [erro, setErro] = useState('');
  /** As mudanças ainda não salvas, por conta. Vazio = nada pendente. */
  const [rascunho, setRascunho] = useState<Record<string, Escolha>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['visibilidade-da-sessao', sessaoId],
    queryFn: () => chamar<VisibilidadeDaSessao>(CAMINHOS.visibilidadeDaSessao(sessaoId)),
    // Só busca quando alguém abre: é uma lista de contas da frota inteira, e
    // ela não tem por que viajar a cada conversa que o painel desenha.
    enabled: aberto,
  });

  const salvar = useMutation({
    mutationFn: (excecoes: { usuarioId: string; sentido: SentidoDoAcesso }[]) =>
      chamar<VisibilidadeDaSessao>(CAMINHOS.visibilidadeDaSessao(sessaoId), {
        method: 'PUT',
        corpo: { excecoes },
      }),
    onSuccess: () => {
      setErro('');
      setRascunho({});
      void cliente.invalidateQueries({ queryKey: ['visibilidade-da-sessao', sessaoId] });
      // A lista de conversas de quem perdeu/ganhou acesso mudou de tamanho.
      void cliente.invalidateQueries({ queryKey: ['sessoes'] });
    },
    onError: (e) => setErro(mensagemDoErro(e, 'Não foi possível salvar.', ERROS)),
  });

  const espectadores = data?.espectadores ?? [];
  // Quem administra não recebe linha: a exceção não valeria para ela, e o
  // servidor recusa gravá-la. Mostrar a escolha seria oferecer o que não há.
  const editaveis = espectadores.filter((e) => e.papel !== 'ADMIN');
  const escolhaDe = (e: EspectadorDaSessao): Escolha =>
    rascunho[e.usuarioId] ?? (e.excecao ?? 'HERDA');
  const pendente = Object.keys(rascunho).length > 0;

  const aplicar = (): void => {
    const excecoes = editaveis
      .map((e) => ({ usuarioId: e.usuarioId, sentido: escolhaDe(e) }))
      .filter((e): e is { usuarioId: string; sentido: SentidoDoAcesso } => e.sentido !== 'HERDA');
    salvar.mutate(excecoes);
  };

  return (
    <div className="shrink-0 space-y-2 border-t border-zinc-800 px-3 py-2">
      <BotaoLeve onClick={() => setAberto((v) => !v)} aria-expanded={aberto}>
        {aberto ? 'Fechar quem vê' : 'Quem vê esta conversa'}
      </BotaoLeve>
      {aberto ? (
        <div
          className="space-y-2 rounded-md border border-zinc-700 bg-zinc-900 p-3"
          data-teste="quem-ve-esta-conversa"
        >
          {erro ? (
            <p className="rounded-md border border-red-900 bg-red-950/40 px-2 py-1.5 text-xs text-red-200">
              {erro}
            </p>
          ) : null}
          <p className="text-xs text-zinc-500">
            A base é o projeto (a pasta desta máquina). Aqui só ficam as exceções desta conversa.
          </p>
          {isLoading ? (
            <p className="text-xs text-zinc-500">Carregando…</p>
          ) : editaveis.length === 0 ? (
            <p className="text-xs text-zinc-500">
              Só há contas de administrador, e elas veem toda conversa.
            </p>
          ) : (
            /*
              Teto em `vh` e rolagem própria, pela mesma razão do painel de
              contas: este bloco vive acima do compositor, e nada acima dele
              passa de metade da tela — sem teto ele transborda o painel, que
              é `overflow-hidden`, e leva o botão de salvar para fora do
              recorte sem barra de rolagem avisando que ele existe.
            */
            <div className="max-h-[32vh] space-y-2 overflow-y-auto" data-teste="lista-de-espectadores">
              {editaveis.map((e) => (
                <div key={e.usuarioId} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-xs text-zinc-200">
                      {e.nome}{' '}
                      <span
                        className={e.ve ? 'text-emerald-400' : 'text-zinc-600'}
                        data-teste={e.ve ? 've' : 'nao-ve'}
                      >
                        {e.ve ? '· vê' : '· não vê'}
                      </span>
                    </p>
                    <p className="truncate text-[11px] text-zinc-600">{MOTIVOS[e.motivoBase]}</p>
                  </div>
                  <CampoDeEscolha<Escolha>
                    // O nome entra no rótulo porque há um destes por linha:
                    // "Acesso" repetido não diz de quem é.
                    rotulo={'Acesso de ' + e.nome}
                    rotuloOculto
                    compacto
                    valor={escolhaDe(e)}
                    opcoes={[...ESCOLHAS]}
                    aoEscolher={(escolha) =>
                      setRascunho((r) => ({ ...r, [e.usuarioId]: escolha }))
                    }
                  />
                </div>
              ))}
            </div>
          )}
          {/* Rodapé fora da área que rola: o botão que decide não se persegue. */}
          <div className="flex justify-end gap-2 border-t border-zinc-800 pt-2">
            <BotaoLeve onClick={() => setRascunho({})} disabled={!pendente}>
              Descartar
            </BotaoLeve>
            <BotaoLeve onClick={aplicar} disabled={!pendente || salvar.isPending}>
              {salvar.isPending ? 'salvando…' : 'Salvar'}
            </BotaoLeve>
          </div>
        </div>
      ) : null}
    </div>
  );
}
